import React, { useEffect, useState, useCallback, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../app/Api/ApiSlice";
import Loader from "../components/Loader";
import UserPostCard from "../components/UserPostCard";
import OrgPostCard from "../components/OrgPostCard";

const PAGE_SIZE = 10;

const Bookmarks = () => {
    const navigate = useNavigate();
    const [bookmarks, setBookmarks] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

    // Use a ref to prevent overlapping requests
    const abortControllerRef = useRef(null);

    const loadBookmarks = useCallback(async (pageNumber, isInitial = false) => {
        // Prevent duplicate calls for the same page
        if (!isInitial && isFetchingNextPage) return;

        try {
            if (isInitial) {
                setIsInitialLoading(true);
            } else {
                setIsFetchingNextPage(true);
            }

            const res = await api.get("/feeds/bookmarks", {
                params: {
                    page: pageNumber,
                    limit: PAGE_SIZE,
                },
            });

            const newBookmarks = res.data?.bookmarks || [];

            setBookmarks((prev) =>
                pageNumber === 1 ? newBookmarks : [...prev, ...newBookmarks]
            );

            // Robust check for hasMore based on API response or length
            const moreAvailable = res.data?.hasMore ?? newBookmarks.length === PAGE_SIZE;
            setHasMore(moreAvailable);
            setPage(pageNumber + 1);

        } catch (error) {
            if (error.name !== 'CanceledError') {
                console.error("Fetch error:", error);
                toast.error("Failed to load bookmarks");
            }
        } finally {
            setIsInitialLoading(false);
            setIsFetchingNextPage(false);
        }
    }, [isFetchingNextPage]);

    useEffect(() => {
        loadBookmarks(1, true);
        return () => abortControllerRef.current?.abort();
    }, []);

    const removeBookmark = async (e, postId) => {
        e.stopPropagation(); // Prevents navigating to the post detail
        try {
            await api.delete(`/bookmarks/${postId}`);
            setBookmarks((prev) => prev.filter((item) => item.post?._id !== postId));
            toast.success("Bookmark removed");
        } catch (error) {
            toast.error("Failed to remove bookmark");
        }
    };

    // Helper to render the correct card type
    const renderPostCard = (bookmark) => {
        switch (bookmark.postType) {
            case "userPost":
                return <UserPostCard userPost={bookmark} />;
            case "orgPost": // Added missing case
                return <OrgPostCard orgPost={bookmark} />;
            default:
                return null;
        }
    };

    if (isInitialLoading) return <Loader />;

    return (
        <div className="bookmarks-page page-content">
            <h2 className="page-title">Bookmarks</h2>

            {bookmarks.length === 0 ? (
                <div className="empty-state">
                    <p>You haven’t bookmarked any posts yet.</p>
                </div>
            ) : (
                <InfiniteScroll
                    dataLength={bookmarks.length}
                    next={() => loadBookmarks(page)}
                    hasMore={hasMore}
                    loader={<p className="loading-indicator">Loading more...</p>}
                    endMessage={<p className="end-indicator">You’ve reached the end.</p>}
                >
                    <div className="bookmarks-list">
                        {bookmarks.map((bookmark) => (
                            <div
                                key={bookmark._id}
                                className="bookmark-card"
                                style={{ cursor: 'pointer', position: 'relative' }}
                            // onClick={() => navigate(`/posts/${bookmark.post?._id}`)}
                            >
                                <div className="bookmark-content">
                                    {renderPostCard(bookmark)}
                                </div>

                                <button
                                    className="remove-bookmark-btn"
                                    onClick={(e) => removeBookmark(e, bookmark.post?._id)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </InfiniteScroll>
            )}
        </div>
    );
};

export default Bookmarks;